import { TrueType } from 'library/ts/utilities/modules/enums/TrueType';
import { isIterable } from 'library/ts/utilities/modules/Type';

function isMergeableObject( val ) {
	const nonNullObject = val && typeof val === TrueType.OBJECT;

	return nonNullObject
		&& Object.prototype.toString.call( val ) !== '[object RegExp]'
		&& Object.prototype.toString.call( val ) !== '[object Date]';
}

function emptyTarget( val ) {
	return Array.isArray( val ) ? [] : {};
}

function cloneIfNecessary( value, optionsArgument ) {
	const clone = optionsArgument && optionsArgument.clone === true;
	return ( clone && isMergeableObject( value ) ) ? deepmerge( emptyTarget( value ), value, optionsArgument ) : value;
}

function defaultArrayMerge( target, source, optionsArgument ) {
	const destination = target.slice();
	source.forEach( function ( e, i ) {
		if ( typeof destination[ i ] === TrueType.UNDEFINED ) {
			destination[ i ] = cloneIfNecessary( e, optionsArgument );
		} else if ( isMergeableObject( e ) ) {
			destination[ i ] = deepmerge( target[ i ], e, optionsArgument );
		} else if ( target.indexOf( e ) === -1 ) {
			destination.push( cloneIfNecessary( e, optionsArgument ) );
		}
	} );
	return destination;
}

function mergeObject( target, source, optionsArgument ) {
	const destination = {};
	if ( isMergeableObject( target ) ) {
		Object.keys( target ).forEach( function ( key ) {
			destination[ key ] = cloneIfNecessary( target[ key ], optionsArgument );
		} );
	}
	Object.keys( source ).forEach( function ( key ) {
		if ( !isMergeableObject( source[ key ] ) || !target[ key ] ) {
			destination[ key ] = cloneIfNecessary( source[ key ], optionsArgument );
		} else {
			destination[ key ] = deepmerge( target[ key ], source[ key ], optionsArgument );
		}
	} );
	return destination;
}

/**
 * @description Object deep merge library
 * @see {@link https://davidwalsh.name/javascript-deep-merge}
 * @param target
 * @param source
 * @param optionsArgument
 * @return {any}
 */
export function deepmerge( target, source, optionsArgument = undefined ) {
	const array = Array.isArray( source );
	const options = optionsArgument || { arrayMerge: defaultArrayMerge };
	const arrayMerge = options.arrayMerge || defaultArrayMerge;

	if ( array ) {
		return Array.isArray( target ) ? arrayMerge( target, source, optionsArgument ) : cloneIfNecessary( source, optionsArgument );
	} else {
		return mergeObject( target, source, optionsArgument );
	}
}

deepmerge.all = function deepmergeAll( array, optionsArgument ) {
	if ( !Array.isArray( array ) || array.length < 2 ) {
		throw new Error( 'first argument should be an array with at least two elements' );
	}

	// we are sure there are at least 2 values, so it is safe to have no initial value
	return array.reduce( function ( prev, next ) {
		return deepmerge( prev, next, optionsArgument );
	} );
};

/**
 * @description will merge 2 objects *only* if a particular source value is truthy
 * @param {{}} target
 * @param {{}} source
 * @returns {any}
 */
export const mergeOverwriteNull = function ( target = {}, source = {} ): any {
	if ( !isIterable( target ) || !isIterable( source ) ) {
		return target;
	}
	Object.entries( source || {} ).forEach( ( [ key, value ] ) => {
		if ( value ) {
			target[ key ] = value;
		}
	} );

	return target;
};